import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./expenseList.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoader } from "../../../../../components/LoaderC";
import Loader from "../../../../../components/Loader";
import { Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, IconButton, Button, Slide } from "@mui/material";
import ListFilters from "./components/ListFilters";
import ListRecords from "./components/ListRecords";
import ListDonutWidget from "./components/ListDonutWidget";
import { X, Trash2 } from "react-feather";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

const PayeeInitials = ({ payee }) => {
  let payeeIn = '-';
  if (payee) {
    const cleanName = payee.replace(/\//g, " ").trim();
    const parts = cleanName.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "-";
    const initials = parts.length === 1 ? parts[0][0] : parts[0][0] + parts[parts.length - 1][0];
    payeeIn = initials.toUpperCase();
  }

  return <p>{payeeIn}</p>
}

const ExpenseList = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sortItem, setSortItem] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [payeeList, setPayeeList] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectecId, setSelectecId] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterredData, setFilterredData] = useState([]);
  const [currentFilterValue, setCurrentFilterValue] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", status: "", due_date: null, category: "", payee: "" });
  const [propertyList, setPropertyList] = useState([]);
  const [donutGraphData, setDonutGraphData] = useState([]);
  const [apartmentList, setApartmentList] = useState([]);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const { property, apartment, category, payee, status, due_date: dueDate } = filterData;

  useEffect(() => {
    const fetchAllProperties = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        let propertyData = response.data?.data || [];
        setPropertyList(propertyData);

        if (localStorage.getItem("propertyFilterData")) {
          let filterPropertyId = localStorage.getItem("propertyFilterData");
          localStorage.removeItem("propertyFilterData");

          setFilterData((prev) => {
            return { ...prev, property: parseInt(filterPropertyId) };
          });

          let apartmentListTemp = [];
          propertyData?.map((i) => {
            if (i.value === parseInt(filterPropertyId)) {
              apartmentListTemp = i.apartments;
            }
            return null;
          });

          apartmentListTemp = [{ value: "all", label: "All", apartments: [] }].concat(apartmentListTemp);
          setApartmentList(apartmentListTemp);
        }

        if (localStorage.getItem("apartmentFilterData")) {
          let apartmentFilter = localStorage.getItem("apartmentFilterData");
          localStorage.removeItem("apartmentFilterData");
          apartmentFilter = parseInt(apartmentFilter);

          setFilterData((prev) => {
            return { ...prev, apartment: parseInt(apartmentFilter) };
          });
        }
      }).catch((error) => {
        console.error("[Expenses] Failed to load property list for filter", error);
        toast.error(error?.response?.data?.message || "Unable to load properties. Please try again.");
      });
    };
    fetchAllProperties();
  }, [getAccessTokenSilently]);

  const changeSortOrder = (type) => {
    if (type === sortItem) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortOrder("asc");
    }
    setSortItem(type);
  };

  const fetchExpenses = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
    if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
    if (isActiveFilter(property)) params.property_id = property;
    if (isActiveFilter(apartment)) params.apt_id = apartment;
    if (isActiveFilter(category)) params.category_id = category;
    if (isActiveFilter(payee)) params.payee_id = payee;
    if (isActiveFilter(status)) params.status = status;
    if (Array.isArray(dueDate) && dueDate.length === 2) {
      params.start = dayjs(dueDate[0]).format("YYYY-MM-DD");
      params.end = dayjs(dueDate[1]).format("YYYY-MM-DD");
    }
    if (sortItem !== "") {
      params.sort_by = sortItem;
      params.sort_order = sortOrder;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/expenses-paginated", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      // Ignore responses from requests that were superseded by newer filters/sorting
      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};

      setFilterredData((prev) => (replace ? newChunk : prev.concat(newChunk)));
      setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
      setHasMore(Boolean(meta?.has_more));
      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Expenses] Failed to load expense list", error);
      toast.error(error?.response?.data?.message || "Unable to load expenses. Please try again.");

      if (replace) {
        setLoadError(true);
        setFilterredData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, debouncedSearch, property, apartment, category, payee, status, dueDate, sortItem, sortOrder]);

  // Debounce search text input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filterData.searchKey || "");
    }, filterData.searchKey ? 400 : 0);

    return () => clearTimeout(handler);
  }, [filterData.searchKey]);

  // Load the first chunk again when search, filters or sorting change
  useEffect(() => {
    fetchExpenses(0, true);
  }, [fetchExpenses]);

  // Infinite scroll: load the next chunk when a sentinel enters the viewport
  useEffect(() => {
    const desktopNode = sentinelRef.current;
    const mobileNode = mobileSentinelRef.current;
    if (!desktopNode && !mobileNode) return;

    const observer = new IntersectionObserver((entries) => {
      const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchExpenses(offset, false);
      }
    }, { threshold: 0.1 });

    if (desktopNode) observer.observe(desktopNode);
    if (mobileNode) observer.observe(mobileNode);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchExpenses, filterredData.length]);

  useEffect(() => {
    getDonutGraphData("");
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const buildUrl = () => {
      if (currentFilterValue === "search" || currentFilterValue === "status" || currentFilterValue === "payee")
        return;
      const queryParams = [];

      if (filterData.property && filterData.property !== "all") {
        queryParams.push(`property_id=${filterData.property}`);
      }

      if (filterData.apartment && filterData.apartment !== "all") {
        queryParams.push(`apt_id=${filterData.apartment}`);
      }

      if (Array.isArray(filterData.due_date) && filterData.due_date.length === 2) {
        const start = filterData.due_date[0]?.toLocaleDateString("en-CA");
        const end = filterData.due_date[1]?.toLocaleDateString("en-CA");

        queryParams.push(`start=${start}`);
        queryParams.push(`end=${end}`);
      }

      if (filterData.category && filterData.category !== "all") {
        queryParams.push(`category_id=${filterData.category}`);
      }

      let queryParamsUrl = "";

      if (queryParams.length) {
        queryParamsUrl = `?${queryParams.join("&")}`;
      }

      getDonutGraphData(queryParamsUrl);
    };

    if (currentFilterValue) {
      buildUrl();
    }
  }, [currentFilterValue, filterData]);

  useEffect(() => {
    const getCategoryList = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/expense-categories", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setExpenseCategories(response.data?.data || []);

        if (localStorage.getItem("expenseCategoryFilterData")) {
          let categoryFilter = localStorage.getItem("expenseCategoryFilterData");
          localStorage.removeItem("expenseCategoryFilterData");
          categoryFilter = parseInt(categoryFilter);

          setFilterData((prev) => {
            return { ...prev, category: parseInt(categoryFilter) };
          });

          setCurrentFilterValue("category");
        }
      }).catch((error) => {
        console.error("[Expenses] Failed to load category list for filter", error);
        toast.error(error?.response?.data?.message || "Unable to load expense categories. Please try again.");
      });
    };

    getCategoryList();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const getPayeeList = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/payees", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setPayeeList(response.data?.data || []);
      }).catch((error) => {
        console.error("[Expenses] Failed to load payee list for filter", error);
        toast.error(error?.response?.data?.message || "Unable to load payees. Please try again.");
      });
    };

    getPayeeList();
  }, [getAccessTokenSilently]);

  const selectProperty = (e) => {
    let apartmentListTemp = [];
    propertyList?.map((i) => {
      if (i.value === e) {
        apartmentListTemp = i.apartments;
      }
      return null;
    });

    apartmentListTemp = [{ value: "all", label: "All", apartments: [] }].concat(
      apartmentListTemp
    );
    setApartmentList(apartmentListTemp);
  };

  async function getDonutGraphData(queryParams) {
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
    let url = process.env.REACT_APP_PAYMENT_API_URL + "v1/expense-data";

    if (queryParams) {
      url = url + queryParams;
    }
    axios(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((response) => {
      setDonutGraphData(response.data.data);
    }).catch((error) => { });
  }

  const confirmDelete = async () => {
    setDeleteModalOpen(false);
    setDeleting(true);

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios.delete(process.env.REACT_APP_PAYMENT_API_URL + "v1/expenses/" + selectecId, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSnackbarOpen(true);
      setSnackbarMsg(response.data?.message);

      setFilterredData((prev) => prev?.filter((i) => i?.id !== selectecId));
      setOffset((prev) => Math.max(0, prev - 1));
      setSelectecId(0);
    } catch (error) {
      console.error("[Expenses] Failed to delete expense", error);
      setSnackbarOpen(true);
      setSnackbarMsg(error?.response?.data?.message || "Unable to delete the expense. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const renderEmpty = () => (loadError ? (<div className={`${styles.NoDataMain}`}>
    <p className={`${styles.NoDataText}`}>Something went wrong!</p>
    <p className={`${styles.NoDataTextSub}`}>We couldn't load the expenses.</p>
    <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchExpenses(0, true)}>Retry</button>
  </div>) : (<div className={`${styles.NoDataMain}`}>
    <div className={`${styles.NoDataIMG}`}>
      <img src="/static/img/no-expense.png" alt="No Data" />
    </div>
    <p className={`${styles.NoDataText}`}>Expense list is empty.</p>
    <p className={`${styles.NoDataTextSub}`}>Time to track your spending!</p>
  </div>));

  const renderScrollStatus = () => (<>
    {loadingMore && (<div className={`${styles.ScrollStatus}`}>
      <div className={`${styles.scrollLoaderWrap}`}>
        <div className={`${styles.scrollSpinner}`} />
        <span>Loading more expenses...</span>
      </div>
    </div>)}

    {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
      <p>Couldn't load more expenses.</p>
      <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchExpenses(offset, false)}>Retry</button>
    </div>)}
  </>);

  return (<div className={`${styles.BodyCon}`}>
    {deleting && <Loader />}
    <div className={`${styles.mainCardDiv}`}>
      <div className={`${styles.mainCard}`}>
        <div className="control-pane">
          {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
          </div>)}

          <div className={`${styles.Row}`}>
            {!contentLoading && (<>
              <ListFilters
                setFilterData={setFilterData}
                setFilterOpen={setFilterOpen}
                filterOpen={filterOpen}
                filterData={filterData}
                selectProperty={selectProperty}
                propertyList={propertyList}
                apartmentList={apartmentList}
                setCurrentFilterValue={setCurrentFilterValue}
                expenseCategories={expenseCategories}
                payeeList={payeeList}
              />

              <div className={`${styles.IncomeListingBody}`}>
                <div className={refetching ? `${styles.IncomeListSec} ${styles.Refetching}` : `${styles.IncomeListSec}`}>

                  {filterredData.length > 0 && (<ListRecords
                    filterredData={filterredData}
                    sortItem={sortItem}
                    sortOrder={sortOrder}
                    changeSortOrder={changeSortOrder}
                    sentinelRef={sentinelRef}
                    setDeleteModalOpen={setDeleteModalOpen}
                    setSelectecId={setSelectecId}
                  />)}

                  {filterredData.length > 0 && renderScrollStatus()}

                  {filterredData.length === 0 && renderEmpty()}
                </div>

                <div className={`${styles.IncomeGraphSec}`}>
                  <ListDonutWidget donutGraphData={donutGraphData} />
                </div>
              </div>

              <div className={refetching ? `${styles.ListMobileView} ${styles.Refetching}` : `${styles.ListMobileView}`}>
                {filterredData?.map(item => {
                  return (<Link key={item?.id} to={"/expense/all-expense/edit/" + item?.id} className={`${styles.MaintenanceReqCard}`}>
                    <div className={`${styles.CardImgSec} Secondary`}>
                      <PayeeInitials payee={item?.payee_name} />
                    </div>
                    <div className={`${styles.MainReCardCon}`}>
                      <div>
                        <div className={`${styles.MainReCardIdStatus}`}>
                          <p className={`${styles.Title}`}>{item?.payee_name}</p>
                          {item?.file_url && <a href={item.file_url} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); }} className={`${styles.AttatchmentClass}`}><img src="/static/img/bill-icon.svg" alt="" /></a>}
                          <button className={`${styles.ExpenseDeleteBU}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectecId(item.id); setDeleteModalOpen(true); }}><Trash2 /></button>
                        </div>
                        <div className={`${styles.MainReCardIdStatus}`}>
                          <p className={`${styles.MainReID}`}>Submitted: {item?.due_date ? dayjs(item?.due_date).format("MM/DD/YYYY") : " - "}</p>
                          {item?.status === 0 && (<span className={`${styles.StatusBadge} ${styles.Pending}`}>Upcoming</span>)}
                          {item?.status === 1 && (<span className={`${styles.StatusBadge} ${styles.Failed}`}>Due</span>)}
                          {item?.status === 2 && (<span className={`${styles.StatusBadge} ${styles.Confirm}`}>Paid: {dayjs(item?.payment_date).format("MM/DD/YYYY")}</span>)}
                        </div>
                        <div className={`${styles.MainReCardIdStatus}`}>
                          <p className={`${styles.MainReID}`}>{item?.category?.title} <span>${item?.formatted_amount}</span></p>
                        </div>
                        <div className={`${styles.MainReCardIdStatusTwo}`}>
                          <p className={`${styles.MainReID}`}>{item?.property_name} {item?.apt_name ? <span>{item?.apt_name}</span> : ""}</p>
                        </div>
                      </div>
                    </div>
                  </Link>)
                })}

                {/* Sentinel: entering the viewport loads the next chunk on mobile */}
                {filterredData.length > 0 && (<div ref={mobileSentinelRef} className={`${styles.ScrollSentinel}`} />)}

                {filterredData.length > 0 && renderScrollStatus()}

                {filterredData.length === 0 && renderEmpty()}
              </div>
            </>)}
          </div>
        </div>
      </div>
    </div>
    <Dialog
      open={deleteModalOpen}
      onClose={(e) => setDeleteModalOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Would you like to delete this expense?
        <button onClick={(e) => setDeleteModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">The expense information will be removed immediately.You can't undo this action.</label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => confirmDelete()} className="CancelPopupBU">Delete</Button>
        <Button onClick={(e) => setDeleteModalOpen(false)} className="SubmitPopupBU">Cancel</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />
  </div >);
};

export default ExpenseList;